import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { InconsistencyFact } from '../../../../../backend/src/matter/inconsistency/inconsistency-fact.entity';
import { Chip } from '@/new/components/chip';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';

export function DocComparerGroup({ title, facts }: { title: string; facts: InconsistencyFact[] }) {
  const { subsetId } = useParams();
  if (!subsetId) return null;

  const [activeFactIndex, setActiveFactIndex] = useState(0);
  const [activeLocationIndex, setActiveLocationIndex] = useState(0);

  const currentFact = useMemo(() => facts[activeFactIndex], [activeFactIndex]);
  const currentLocation = useMemo(
    () => currentFact.fact.locations[activeLocationIndex],
    [currentFact, activeLocationIndex],
  );

  // facts might have multiple locations, so cycle through them first
  function getNextFact() {
    const locationCount = currentFact.fact.locations.length;

    if (activeLocationIndex === locationCount - 1) {
      // last location, go to new fact
      setActiveFactIndex(activeFactIndex + 1);
    } else {
      // still other locations, go to new location
      setActiveLocationIndex(activeLocationIndex + 1);
    }
  }

  function getPrevFact() {
    if (activeLocationIndex === 0) {
      // first location, go to prev fact
      setActiveFactIndex(activeFactIndex - 1);
    } else {
      // still other locations, go to prev location
      setActiveLocationIndex(activeLocationIndex - 1);
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-hidden">
      <div className="mx-2 my-2 flex items-center justify-between gap-3">
        {/* 'title' is like Claim: <doc> (for example) */}
        <p className="line-clamp-1 text-base font-medium break-all">{title}</p>

        <div className="flex items-center gap-1.5">
          <Button size="sm" onClick={getPrevFact} disabled={activeFactIndex === 0 && activeLocationIndex === 0}>
            <Icon className="lucide--arrow-left" />
            <span>Prev Fact</span>
          </Button>

          <Button
            size="sm"
            onClick={getNextFact}
            disabled={
              activeFactIndex === facts.length - 1 && activeLocationIndex === currentFact.fact.locations.length - 1
            }
          >
            <span>Next Fact</span>
            <Icon className="lucide--arrow-right" />
          </Button>
        </div>
      </div>

      <div className="min-h-0 grow overflow-hidden rounded-md border">
        <FactDocViewer
          className="h-full"
          factId={currentFact.fact.id}
          caseId={subsetId}
          docId={currentLocation.doc.id}
          hideFeatureButtons={false}
          hideCloseButton={true}
        />
      </div>

      <div className="text-xs">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <p className="text-blue-600">{currentLocation.doc.file.name}</p>
            {currentLocation.doc.type && <Chip className={`${CHIP_COLOURS.type}`}>{currentLocation.doc.type}</Chip>}
          </div>
        </div>

        <p className="mt-1 mb-3 text-gray-600">{facts[activeFactIndex].fact.description}</p>
      </div>
    </div>
  );
}
